/**
 * Formats a string from PascalCase or snake_case to Title Case with proper spacing.
 *
 * Examples:
 * - "EventType" → "Event Type"
 * - "workflow_started" → "Workflow Started"
 * - "eventType" → "Event Type"
 * - "APIKey" → "API Key"
 *
 * @param value - The string to format (can be PascalCase, snake_case, or already formatted)
 * @returns Formatted string in Title Case, or empty string if input is empty/null/undefined
 */
export const formatString = (value: string | null | undefined): string => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // If string is already formatted (contains spaces and proper casing), return as-is
  if (value.includes(' ') && value.split(' ').every((word) => /^[A-Z]/.test(word.trim()))) {
    return value;
  }

  // Handle snake_case
  if (value.includes('_')) {
    return value
      .split('_')
      .filter((part) => part.length > 0)
      .map((part) => {
        // Capitalize first letter and lowercase the rest
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join(' ');
  }

  // Handle PascalCase or camelCase
  // Split on capital letters, but preserve consecutive capitals (like "API")
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Add space between consecutive capitals and following lowercase
    .split(' ')
    .filter((part) => part.length > 0)
    .map((part) => {
      // Ensure first letter is uppercase
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};
